import type { SelectListProps, SelectPopupProps, SelectPositionerProps } from '@base-ui/react';

import { Select as BaseSelect } from '@base-ui/react';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { useTranslation } from 'react-i18next';

import { useCollisionPadding } from '~/hooks/useCollisionPadding';
import { clsxs } from '~/styles/utils';

import { emptyStyle, listStyle, popupMinWidth, popupStyle, positionerStyle } from '../form.css';

type Props = Omit<SelectPopupProps, 'children'> &
  Pick<SelectListProps, 'children'> & {
    align?: SelectPositionerProps['align'];
    emptyMessage?: string;
    minWidth?: string;
  };

export const SelectList = ({ align = 'center', children, emptyMessage, minWidth, ...delegated }: Props) => {
  const collisionPadding = useCollisionPadding();

  const { t } = useTranslation(['common']);

  const isEmpty = Array.isArray(children) ? children.length === 0 : !children;

  return (
    <BaseSelect.Portal>
      <BaseSelect.Positioner
        align={align}
        alignItemWithTrigger={false}
        className={positionerStyle}
        collisionPadding={collisionPadding}
        sideOffset={4}
      >
        <BaseSelect.Popup
          {...delegated}
          className={clsxs(popupStyle, delegated.className)}
          style={(state) => ({
            ...(minWidth && assignInlineVars({ [popupMinWidth]: `${minWidth}` })),
            ...(typeof delegated.style === 'function' ? delegated.style(state) : delegated.style),
          })}
        >
          {!isEmpty ? (
            <BaseSelect.List className={listStyle}>{children}</BaseSelect.List>
          ) : (
            <div className={emptyStyle}>{emptyMessage ?? t('common:listbox.noOptions')}</div>
          )}
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  );
};
